import { Injectable } from '@angular/core';

const STORAGE_KEY = 'mojito-bi.auth-attempts';

/**
 * How many consecutive failed callbacks to tolerate before showing the error
 * page instead of trying again. One retry covers a genuine hiccup; a second
 * failure means the cause is not transient.
 */
const MAX_ATTEMPTS = 2;

/**
 * The circuit breaker for the login round trip.
 *
 * Nothing else in the stack has one: the library's `login()` has no
 * re-entrancy guard, and a failed callback used to navigate straight back into
 * the guarded route tree, which calls `login()` again. Since the IdP still has
 * a valid session cookie it answers each authorize instantly, so any permanent
 * validation failure became a tight, unbounded authorize/callback loop.
 *
 * The count lives in `sessionStorage` because it has to survive the full-page
 * redirect to the IdP and back, but must not outlive the tab.
 */
@Injectable()
export class AuthAttemptService {
  /**
   * Records a failed callback. Returns true once the limit is reached, meaning
   * the caller must stop retrying.
   */
  recordFailure(): boolean {
    const attempts = this.read() + 1;

    try {
      sessionStorage.setItem(STORAGE_KEY, String(attempts));
    } catch {
      // Storage disabled: fall back to stopping immediately rather than
      // risking an unbounded loop we cannot count.
      return true;
    }

    return attempts >= MAX_ATTEMPTS;
  }

  /** Clears the count. Call after any successful authentication. */
  reset(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do — a stale count only costs one extra error screen.
    }
  }

  private read(): number {
    try {
      const parsed = Number(sessionStorage.getItem(STORAGE_KEY));

      return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
    } catch {
      return 0;
    }
  }
}
